import { useState, useEffect, useCallback } from 'react'
import * as changeCase from 'change-case'
import { Button } from '@klear/ui/components/button'
import { Alert, AlertDescription } from '@klear/ui/components/alert'
import StripeLogo from '@/assets/logo-stripe.svg'
import { RotateCw, CheckCircle, AlertTriangle, ArrowUpRight, RefreshCw, AlertCircle } from 'lucide-react'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { useKyc } from '@klear/ui/hooks/use-kyc'
import { useEventSource } from '@klear/ui/hooks/use-event-source'

import { Badge } from '@klear/ui/components/badge'
import { useAuth } from '@klear/ui/contexts/auth-context'
import {
  PERMISSION_ACTION_EDIT,
  PERMISSION_ENTITY_TREASURY,
  ROUTE_IDENTITY_VERIFICATION,
  ROUTE_SSE_STREAM
} from '@klear/constants'
import { SSE_EVENT_TYPE_KYC_UPDATE } from '@klear/sse'
import { getApiUrl, getAppEnv } from '@klear/runtime-env'
import { isProductionAppEnv } from '@klear/constants/app-env'
import { useParams, useSearchParams, useNavigate } from 'react-router'
import toPairs from 'lodash/toPairs'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export default function KYC() {
  useDocumentTitle('Identity Verification')
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [redirecting, setRedirecting] = useState(false)
  const { traderId } = useParams()
  const { trader } = useTrader(traderId)
  const { kyc, stripeCreate, stripeRefreshStatus, stripeGetVerificationLink } = useKyc()

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const shouldRefresh = searchParams.get('shouldRefresh') === '1'

  const handleRefreshStatus = useCallback(async () => {
    try {
      setLoading(true)
      await stripeRefreshStatus()
    } catch (error) {
      console.error('Error refreshing Stripe status', error)
    } finally {
      setLoading(false)
    }
  }, [stripeRefreshStatus])

  const handleVerifyRequirements = useCallback(async () => {
    try {
      setRedirecting(true)
      const url = await stripeGetVerificationLink()
      if (url) {
        window.location.assign(url)
      }
    } finally {
      setRedirecting(false)
    }
  }, [stripeGetVerificationLink])

  const handleSSEMessage = useCallback(
    async (data: { type?: string; organizationId?: number }) => {
      if (data.type === SSE_EVENT_TYPE_KYC_UPDATE && data.organizationId === trader?.organizationId) {
        await handleRefreshStatus()
      }
    },
    [trader?.organizationId, handleRefreshStatus]
  )

  const handleSSEError = useCallback((error: Event) => {
    console.error('SSE connection error:', error)
  }, [])

  const sseUrl = trader?.organizationId
    ? `${getApiUrl()}/${ROUTE_IDENTITY_VERIFICATION}/${ROUTE_SSE_STREAM}?traderId=${trader?.id}`
    : ''
  useEventSource(sseUrl, {
    messageType: SSE_EVENT_TYPE_KYC_UPDATE,
    onMessage: handleSSEMessage,
    onError: handleSSEError
  })

  useEffect(() => {
    if (shouldRefresh) {
      searchParams.delete('shouldRefresh')
      navigate({ search: searchParams.toString() }, { replace: true })
      void handleRefreshStatus()
    }
  }, [shouldRefresh, searchParams, navigate, handleRefreshStatus])

  const handleCreateStripeAccount = async () => {
    try {
      setLoading(true)
      await stripeCreate()
      await handleVerifyRequirements()
    } catch (error) {
      console.error('Error creating Stripe account', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="container mx-auto px-2 py-6 space-y-6">
      <div className="flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Identity Verification</h2>
          <p className="inline-flex items-center text-muted-foreground mb-8">
            Verify your organization&apos;s identity with <img src={StripeLogo} alt="Stripe Logo" className="w-16" />
          </p>
        </div>
      </div>
      <div className="flex flex-col items-center space-y-4">
        {!kyc?.id ? (
          <Button
            onClick={handleCreateStripeAccount}
            disabled={loading || !user?.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT)}
            className="w-fit"
          >
            <RotateCw className="mr-2 h-4 w-4" />
            Connect Stripe
          </Button>
        ) : redirecting ? (
          <div className="flex items-center space-x-2">
            <RotateCw className="h-4 w-4 animate-spin" />
            <span>Preparing verification steps...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-4 w-full max-w-4xl">
            <div className="w-full space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <h4 className="font-semibold">Account Status</h4>
                  {!kyc.pastDueRequirements.length ? (
                    <Badge variant="default" className="flex items-center">
                      <CheckCircle className="mr-1 h-4 w-4" />
                      Verified
                    </Badge>
                  ) : (
                    <>
                      <Badge variant="outline" className="flex items-center mr-2">
                        <CheckCircle className="mr-1 h-4 w-4" />
                        Connected
                      </Badge>
                      <Badge variant="destructive" className="flex items-center">
                        <AlertCircle className="mr-1 h-4 w-4" />
                        Verification Required
                      </Badge>
                    </>
                  )}
                </div>
                {!isProductionAppEnv(getAppEnv()) &&
                  user?.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT) && (
                    <Button variant="outline" onClick={handleRefreshStatus} disabled={loading}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Refresh
                    </Button>
                  )}
              </div>

              <div className="rounded-lg border p-4">
                <h5 className="font-medium mb-2">Business Information</h5>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Business Name:</span>
                    <span className="ml-2">{kyc.businessName}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Country:</span>
                    <span className="ml-2">{kyc.country}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Business Type:</span>
                    <span className="ml-2">{kyc.businessType}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>
                    <span className="ml-2">{kyc.email}</span>
                  </div>
                </div>
              </div>

              {kyc.errors.length > 0 && (
                <div className="rounded-lg border border-destructive p-4">
                  <h5 className="font-medium mb-2">Account Level Issues</h5>
                  <div className="space-y-2">
                    {kyc.errors.map((error, index) => (
                      <div key={index} className="flex items-start space-x-2 text-sm text-destructive">
                        <AlertTriangle className="h-4 w-4 mt-0.5" />
                        <span>{error.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {kyc.requirementsTypes.length > 0 && (
                <div className="rounded-lg border p-4">
                  <h5 className="font-medium mb-2">Outstanding Requirements</h5>
                  <div className="grid grid-cols-2 gap-2">
                    {kyc.requirementsTypes.map((type, index) => (
                      <div key={index} className="flex items-start space-x-2 text-sm">
                        <AlertTriangle className="h-4 w-4 mt-0.5" />
                        <span>{type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {kyc.persons.map((person, personIndex) => (
                <div
                  key={personIndex}
                  className={`rounded-lg border p-4 ${kyc.hasPersonOutstandingRequirements ? 'border-destructive' : ''}`}
                >
                  <h5 className="font-medium mb-2">People</h5>
                  <h6 className="text-sm text-muted-foreground mb-2">
                    {person.firstName} {person.lastName}{' '}
                    <Badge variant={person.verificationStatus === 'verified' ? 'default' : 'secondary'}>
                      {person.verificationStatus}
                    </Badge>
                  </h6>
                  {person.errors.map((error, index) => (
                    <div key={index} className="mb-2 text-sm text-destructive">
                      <AlertTriangle className="inline-block mr-1 h-4 w-4" />
                      {error.reason}
                    </div>
                  ))}

                  {person.currentlyDue.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium mb-1">Required Items:</p>
                      <ul className="list-disc list-inside text-sm">
                        {person.currentlyDue.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {person.alternatives.length > 0 && (
                    <div className="mt-2 text-sm">
                      <p className="font-medium">Alternative Options:</p>
                      {person.alternatives.map((alt, index) => (
                        <div key={index}>
                          <p>Provide either:</p>
                          <ul className="list-disc list-inside">
                            <li>{alt.originalFieldsDue.join(', ')}</li>
                            <p className="ml-4">OR</p>
                            <li>{alt.alternativeFieldsDue.join(', ')}</li>
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <div className="rounded-lg border p-4">
                <h5 className="font-medium mb-2">Payment Capabilities</h5>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  {toPairs(kyc?.capabilities || {}).map(([key, value]) => {
                    const capabilityValue = value as string
                    return (
                      <div className="flex items-center justify-between" key={key}>
                        <span className="text-muted-foreground">{changeCase.sentenceCase(key)}:</span>
                        <Badge variant={capabilityValue === 'active' ? 'default' : 'secondary'} className="ml-2">
                          {capabilityValue}
                        </Badge>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
            {kyc.currentRequirements.length > 0 && (
              <>
                <Alert variant="destructive">
                  <AlertDescription>
                    You have outstanding requirements.{' '}
                    {kyc.currentDeadline != null
                      ? `Please complete them by ${kyc.currentDeadline.toKlearDate().toString()}.`
                      : ''}
                  </AlertDescription>
                </Alert>
                {user?.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT) && (
                  <div className="flex space-x-4">
                    <Button onClick={handleVerifyRequirements} disabled={loading || redirecting}>
                      <ArrowUpRight className="mr-2 h-4 w-4" />
                      Verify Requirements
                    </Button>
                  </div>
                )}
              </>
            )}
            {kyc.isVerified &&
              kyc.currentRequirements.length === 0 &&
              user?.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_EDIT) && (
                <div className="flex space-x-4">
                  <Button onClick={handleVerifyRequirements} disabled={loading || redirecting}>
                    <ArrowUpRight className="mr-2 h-4 w-4" />
                    Update Information
                  </Button>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  )
}
